import {VisibilityOffOutlined, VisibilityOutlined} from "@mui/icons-material";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import React, {KeyboardEvent, useState} from "react";
import {
    Alert,
    alpha,
    CssBaseline,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    ThemeProvider
} from "@mui/material";
import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import {useTranslation} from "react-i18next";
import AuthService from "../../../api/authService";
import {useAppDispatch} from "../../../store";
import {updateRequiredLogin} from "../../../store/app/Slice";
import {useTheme} from "@mui/material/styles";
import FabLanguageDropdown from "../../shared/language-dropdown/FabLanguageDropdown";
import genie_logo from "../../../assets/images/logo.svg";


const RequiredLogin = () => {
    const {t} = useTranslation()
    const [email, setEmail] = useState<string>("")
    const [password, setPassword] = useState<string>("")
    const [showPassword, setShowPassword] = useState<boolean>(false)
    const [error, setError] = useState<boolean>(false)
    const [loading, setLoading] = useState(false)
    const dispatch = useAppDispatch()
    const theme = useTheme()

    const handleOnKeyPress = async (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            await handleOnLoginClick()
        }
    }

    const handleOnLoginClick = async () => {
        if (!email.includes("@genie")) {
            setError(true)
            return
        }
        setError(false)
        setLoading(true)
        try {
            await AuthService.login(email, password)
            setLoading(false)
            dispatch(updateRequiredLogin(false))
        } catch (_) {
            setLoading(false)
            setError(true)
        }
    }

    const handleClickShowPassword = () => {
        setShowPassword((oldShowPassword) => !oldShowPassword)
    }

    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <CssBaseline/>
            <Container component="main" maxWidth="sm" sx={{
                mt: '6em',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                gap: '2em',
                alignItems: 'center',
            }}>
                <img src={genie_logo} width={300} alt="genie-logo"/>
                <Paper elevation={5} sx={{
                    padding: '1em',
                    backgroundColor: alpha(theme.palette.primary.main, .1),
                    backdropFilter: "blur(5px)",
                }}>
                    <Box sx={{mt: 1}}>
                        {error ? <Alert severity="error">{t('error_occurred')}</Alert> : null}
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            label={t('e-mail')}
                            name="email"
                            autoComplete="email"
                            color="primary"
                            onKeyDown={handleOnKeyPress}
                        />
                        <FormControl margin="normal" fullWidth variant="outlined">
                            <InputLabel required
                                        color="primary"
                                        htmlFor="password">{t('password')}</InputLabel>
                            <OutlinedInput
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                required
                                color="primary"
                                autoComplete="current-password"
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={handleOnKeyPress}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            onMouseDown={(e) => e.preventDefault()}
                                            color="primary"
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOffOutlined/> : <VisibilityOutlined/>}
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label={t('password')}
                            />
                        </FormControl>

                        <LoadingButton
                            fullWidth
                            loading={loading}
                            variant="contained"
                            color="primary"
                            onClick={handleOnLoginClick}
                            sx={{mt: 3, mb: 1}}
                        >
                            {t('login')}
                        </LoadingButton>
                    </Box>
                </Paper>
                <FabLanguageDropdown/>
            </Container>
        </ThemeProvider>
    )
}
export default RequiredLogin
